import type { TopMethod } from '../../api/stats';
import { rangeWindow, type RangeKey, type Window } from './shared';

// Comparing two periods of the same length on the Performance tab: the period shown (ending now or on a chosen day,
// or a span zoomed into on a chart) against the one before it, the same period a year earlier, or one ending on a
// chosen day. Pure, so it is tested.

/** `?vs=`: '' = no comparison, `prev`, `year`, or the last UTC day (`YYYY-MM-DD`) of the other period. */
export type CompareKey = '' | 'prev' | 'year' | string;

const DAY = 86_400_000;
const HOUR = 3_600_000;
const isDay = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`));
const endOfDay = (day: string) => Date.parse(`${day}T00:00:00Z`) + DAY;

/** The period shown: ending now, or at the end of the UTC day `end` (the last day included). */
export function periodWindow(key: RangeKey, end: string, now: number = Date.now()): Window {
  return rangeWindow(key, isDay(end) ? Math.min(endOfDay(end), now) : now);
}

/** A zoom (`?from=&to=`) shorter than this is not taken: below a few minutes there is nothing left to see. */
export const MIN_ZOOM_MS = 5 * 60_000;

/** A window over exact times (a zoom); null when the times are not a usable span. */
export function spanWindow(from: number, to: number): Window | null {
  if (!Number.isFinite(from) || !Number.isFinite(to) || to - from < MIN_ZOOM_MS) return null;
  const hours = (to - from) / HOUR;
  return {
    from: new Date(from).toISOString(),
    to: new Date(to).toISOString(),
    fromDay: new Date(from).toISOString().slice(0, 10),
    toDay: new Date(to - 1).toISOString().slice(0, 10),
    bucket: hours <= 168 ? '1h' : '1d',
    hours,
  };
}

/** The zoomed window of `?from=&to=` (ISO times), null when there is none. */
export const zoomWindow = (from: string, to: string): Window | null =>
  from && to ? spanWindow(Date.parse(from), Date.parse(to)) : null;

/** The buckets of `performance/series`; below an hour the collector computes them from the raw measurements. */
export type PerfBucket = '1m' | '5m' | '15m' | '1h' | '1d';

/** The collector keeps the raw measurements this long (`Retention:ValuesDays`): older windows get hourly points. */
export const RAW_DAYS = 90;

/** The bucket for a window: daily beyond 7 days, hourly beyond 24 h, then 15 / 5 / 1 minutes down to an hour. */
export function perfBucket(w: Window, now: number = Date.now()): PerfBucket {
  if (w.bucket === '1d') return '1d';
  // an hour of margin: the collector checks against its own clock
  if (Date.parse(w.from) < now - RAW_DAYS * DAY + HOUR) return '1h';
  return w.hours <= 1 ? '1m' : w.hours <= 4 ? '5m' : w.hours <= 24 ? '15m' : '1h';
}

/** The collector returns the single calls of up to 2 h (`performance/points`), within its raw-values retention. */
export const POINTS_MAX_HOURS = 2;
/** Below this the duration chart shows the single calls unless `?view=lines`. */
export const POINTS_DEFAULT_HOURS = 0.5;

export const canShowPoints = (w: Window, now: number = Date.now()) =>
  w.hours <= POINTS_MAX_HOURS && Date.parse(w.from) >= now - RAW_DAYS * DAY + HOUR;

export const bucketMs = (b: PerfBucket) =>
  ({ '1m': 60_000, '5m': 300_000, '15m': 900_000, '1h': HOUR, '1d': DAY })[b];

export const bucketLabel = (b: PerfBucket) =>
  ({ '1m': 'per minute', '5m': 'per 5 minutes', '15m': 'per 15 minutes', '1h': 'hourly', '1d': 'daily' })[b];

/** The period to compare with, the same length as `main`; null without a comparison. A zoom compares like for like. */
export function compareWindow(key: RangeKey, main: Window, vs: CompareKey, zoomed = false): Window | null {
  if (zoomed) return compareSpan(main, vs);
  if (vs === 'prev') return rangeWindow(key, Date.parse(main.from));
  if (vs === 'year') {
    const d = new Date(main.to);
    d.setUTCFullYear(d.getUTCFullYear() - 1);
    return rangeWindow(key, d.getTime());
  }
  return isDay(vs) ? rangeWindow(key, endOfDay(vs)) : null;
}

/** The span compared with a zoom: right before it, a year earlier, or at the same time of day ending on day `vs`. */
function compareSpan(main: Window, vs: CompareKey): Window | null {
  const from = Date.parse(main.from);
  const to = Date.parse(main.to);
  const len = to - from;
  if (vs === 'prev') return spanWindow(from - len, from);
  if (vs === 'year') {
    const d = new Date(to);
    d.setUTCFullYear(d.getUTCFullYear() - 1);
    return spanWindow(d.getTime() - len, d.getTime());
  }
  if (!isDay(vs)) return null;
  const end = Date.parse(`${vs}T00:00:00Z`) + ((((to - 1) % DAY) + DAY) % DAY) + 1;
  return spanWindow(end - len, end);
}

/** "Oct 22 – Oct 28" (UTC days, the last one included), or "Oct 28, 10:00 – Oct 29, 10:00" for 24 h. */
export function periodLabel(w: Window): string {
  const day = (t: number) =>
    new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const from = Date.parse(w.from);
  const to = Date.parse(w.to);
  if (w.hours <= 24 || from % HOUR !== 0 || to % HOUR !== 0) {
    const at = (t: number) =>
      new Date(t).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      });
    return `${at(from)} – ${at(to)}`;
  }
  return `${day(from)} – ${day(to - 1)}`;
}

/**
 * A large change: p95 (average when a period has no p95) up or down by at least `pct`, by at least `minMs`, with at
 * least `minCalls` calls in both periods so a handful of slow calls does not count.
 */
export const LARGE_CHANGE = { pct: 0.3, minMs: 100, minCalls: 20 };

/** Many more or far fewer calls: at least `pct` up or down and at least `minDiff` calls apart. */
export const LARGE_CALLS = { pct: 0.5, minDiff: 50 };

export interface MethodChange {
  method: string;
  before: TopMethod | null;
  /** Relative change of the timing compared (0.45 = 45 % slower), null when either side has none. */
  change: number | null;
  /** The timing compared: `p95`, or `avg` when a period has no p95. */
  basis: 'p95' | 'avg';
  callsChange: number | null;
  flag: 'slower' | 'faster' | 'new' | null;
  callsFlag: 'more' | 'fewer' | null;
}

const rel = (now: number | null, before: number | null) =>
  now == null || before == null || before <= 0 ? null : now / before - 1;

/** Each current method against the same method in the other period. */
export function methodChanges(
  current: readonly TopMethod[],
  before: readonly TopMethod[]
): Map<string, MethodChange> {
  const old = new Map(before.map((m) => [m.method, m]));
  const out = new Map<string, MethodChange>();
  for (const m of current) {
    const b = old.get(m.method) ?? null;
    const basis = m.p95 != null && b?.p95 != null ? 'p95' : 'avg';
    const now = m[basis];
    const then = b ? b[basis] : null;
    const change = rel(now, then);
    const enough = !!b && m.count >= LARGE_CHANGE.minCalls && b.count >= LARGE_CHANGE.minCalls;
    const large =
      enough &&
      change != null &&
      now != null &&
      then != null &&
      Math.abs(change) >= LARGE_CHANGE.pct &&
      Math.abs(now - then) >= LARGE_CHANGE.minMs;
    const callsChange = rel(m.count, b?.count ?? null);
    const manyCalls =
      !!b &&
      callsChange != null &&
      Math.abs(callsChange) >= LARGE_CALLS.pct &&
      Math.abs(m.count - b.count) >= LARGE_CALLS.minDiff;
    out.set(m.method, {
      method: m.method,
      before: b,
      change,
      basis,
      callsChange,
      callsFlag: manyCalls ? (callsChange > 0 ? 'more' : 'fewer') : null,
      flag: !b
        ? m.count >= LARGE_CHANGE.minCalls
          ? 'new'
          : null
        : large
          ? change > 0
            ? 'slower'
            : 'faster'
          : null,
    });
  }
  return out;
}

/** Calls of the methods in both periods' lists, this period and the other (a method in one list only would skew it). */
export function callTotals(changes: Iterable<MethodChange>, current: readonly TopMethod[]) {
  const count = new Map(current.map((m) => [m.method, m.count]));
  let now = 0;
  let before = 0;
  let methods = 0;
  for (const c of changes) {
    if (!c.before) continue;
    now += count.get(c.method) ?? 0;
    before += c.before.count;
    methods++;
  }
  return { now, before, methods, change: rel(now, before) };
}

/** "+45 %", "−12 %", "±0 %". */
export function fmtChange(c: number | null): string {
  if (c == null) return '—';
  const p = Math.round(c * 100);
  return p === 0 ? '±0 %' : `${p > 0 ? '+' : '−'}${Math.abs(p).toLocaleString()} %`;
}
